import { Header } from "@general-backoffice/core";
import type { BotConfig, ExchangeAccount, PerformancePoint, StrapiId } from "@investhinks/api-client";
import { USER_POPULATE_FULL } from "@investhinks/api-client";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router";
import { Badge, Col, Collapse, Container, Row } from "reactstrap";

import { botConfigsEndpoints, referrerEndpoints } from "../../../../api";
import useBots from "../../../../components/bots/hooks/useBots";
import ChargesTableCard from "../../../../components/charges/ChargesTableCard";
import useCharges from "../../../../components/charges/hooks/useCharges";
import ExchangeAccountCard from "../../../../components/exchangeAccounts/ExchangeAccountCard";
import useExchangeAccounts from "../../../../components/exchangeAccounts/hooks/useExchangeAccounts";
import usePayouts from "../../../../components/payouts/hooks/usePayouts";
import PayoutsTableCard from "../../../../components/payouts/PayoutsTableCard";
import AdminReferralsTableCard from "../../../../components/referral/AdminReferralsTableCard";
import useReferrals from "../../../../components/referral/hooks/useReferrals";
import AmbassadorRequirementsCard from "../../../../components/referrer/partner/AmbassadorRequirementsCard";
import PartnerRecurringRequirementsCard from "../../../../components/referrer/partner/PartnerRecurringRequirementsCard";
import { TaxProfileCard } from "../../../../components/referrer/taxProfile";
import GeneralPerformanceGraphCard from "../../../../components/stats/GeneralPerformanceGraphCard";
import StateSummary from "../../../../components/stats/StateSummary";
import AdminUserOverviewCard from "../../../../components/users/AdminUserOverviewCard";
import AssignReferrerModal from "../../../../components/users/AssignReferrerModal";
import DisableVIPModal from "../../../../components/users/DisableVIPModal";
import EditFreePeriodModal from "../../../../components/users/EditFreePeriodModal";
import useUser from "../../../../components/users/hooks/useUser";
import ManagePartnershipModal from "../../../../components/users/ManagePartnershipModal";
import ManualBillingModal from "../../../../components/users/ManualBillingModal";
import { useAdminViewingUser } from "../../../../contexts/AdminViewingUserContext";
import useFetch from "../../../../hooks/useFetch";
import { canImpersonateUser } from "../../../../utils/impersonation";

interface CurrencyBreakdown {
  currency: string;
  balance: number;
}

interface SectionHeaderProps {
  title: string;
  badge?: string | null;
  isOpen?: boolean;
  onToggle?: () => void;
  collapsible?: boolean;
}

const SectionHeader: React.FC<SectionHeaderProps> = ({ title, badge, isOpen, onToggle, collapsible = false }) => {
  const { t } = useTranslation("users", { keyPrefix: "sections" });

  return (
    <div
      style={{
        ...styles.sectionHeader,
        ...(collapsible ? styles.sectionHeaderClickable : {}),
      }}
      onClick={collapsible ? onToggle : undefined}
    >
      <div style={styles.sectionTitleRow}>
        <h3 style={styles.sectionTitle}>{title}</h3>
        {badge && (
          <Badge color="default" style={styles.sectionBadge}>
            {badge}
          </Badge>
        )}
      </div>
      {collapsible && (
        <span style={styles.collapseToggle}>
          {isOpen ? t("collapse") : t("expand")}
          <i className={`fas fa-chevron-${isOpen ? "up" : "down"} ml-2`} />
        </span>
      )}
    </div>
  );
};

const User = () => {
  const { t } = useTranslation("users", { keyPrefix: "sections" });
  const navigate = useNavigate();
  const { userId } = useParams();
  const userIdNum = userId ? Number(userId) : undefined;
  const { setViewingUser, clearViewingUser } = useAdminViewingUser();

  // State for modals
  const [selectedExchangeAccount, setSelectedExchangeAccount] = useState<ExchangeAccount | null>(null);
  const [isGrantModalOpen, setIsGrantModalOpen] = useState<boolean>(false);
  const [refreshKey, setRefreshKey] = useState<number>(0);
  const [isVIPModalOpen, setIsVIPModalOpen] = useState<boolean>(false);
  const [isPartnershipModalOpen, setIsPartnershipModalOpen] = useState<boolean>(false);
  const [isManualBillingModalOpen, setIsManualBillingModalOpen] = useState<boolean>(false);
  const [isAssignReferrerModalOpen, setIsAssignReferrerModalOpen] = useState<boolean>(false);

  // Collapsible sections state
  const [isPerformanceOpen, setIsPerformanceOpen] = useState<boolean>(false);
  const [isAffiliateOpen, setIsAffiliateOpen] = useState<boolean>(false);
  const [showArchivedAccounts, setShowArchivedAccounts] = useState<boolean>(false);

  // Refs for scrolling into view
  const affiliateRef = useRef<HTMLDivElement | null>(null);
  const performanceRef = useRef<HTMLDivElement | null>(null);

  // Toggle handlers with scroll into view
  const toggleAffiliate = () => {
    const willOpen = !isAffiliateOpen;
    setIsAffiliateOpen(willOpen);
    if (willOpen) {
      setTimeout(() => {
        affiliateRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 100);
    }
  };

  const togglePerformance = () => {
    const willOpen = !isPerformanceOpen;
    setIsPerformanceOpen(willOpen);
    if (willOpen) {
      setTimeout(() => {
        performanceRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 100);
    }
  };

  const {
    user,
    isLoading: isLoadingUser,
    refresh: refreshUser,
  } = useUser(userId, {
    params: {
      populate: USER_POPULATE_FULL,
    },
  });
  const { referrerType, name, referral } = user || {};
  const referrer = referral?.referrer;

  // Set viewing user in context for header to display impersonate button
  useEffect(() => {
    if (user && canImpersonateUser(user)) {
      setViewingUser(user);
    }
    return () => clearViewingUser();
  }, [user, setViewingUser, clearViewingUser]);

  const { exchangeAccounts = [], isLoading: isLoadingExchangeAccounts } = useExchangeAccounts({
    filters: userIdNum ? { user: { id: { $eq: userIdNum } } } : undefined,
    depsToReset: [userId],
    populate: ["bot_configs", "exchange"],
    // Note: Sorting by one-to-many relation fields (bot_configs.*) causes duplicate rows in Strapi
    // Only sort by direct fields on exchange_account to avoid duplicates
    sort: {
      status: "up",
    },
    pagination: { pageSize: 100 },
  });

  // Create a map from exchange account ID to exchange account for O(1) lookup
  const exchangeAccountsById = useMemo(() => {
    const map: Record<StrapiId, ExchangeAccount> = {};
    for (const ea of exchangeAccounts) {
      if (ea?.id) {
        map[ea.id] = ea;
      }
    }
    return map;
  }, [exchangeAccounts]);

  // Fetch ALL bots for this user once instead of per-card to avoid N+1 API calls
  const { bots = [], isLoading: isLoadingBots } = useBots({
    filters: { user: { id: userId } },
    depsToReset: [userId],
    pagination: { pageSize: 100 },
    cacheKey: `user_bots_${userId}`,
  });

  // Create a map from exchange account ID to bot for O(1) lookup
  const botsByExchangeAccountId = useMemo(() => {
    const map: Record<StrapiId, BotConfig> = {};
    for (const bot of bots) {
      const ea = bot?.exchange_account;
      const eaId = typeof ea === "object" ? ea?.id : ea;
      if (eaId && !map[eaId]) {
        map[eaId] = bot;
      }
    }
    return map;
  }, [bots]);

  // Split accounts into visible and archived (hidden)
  // This matches how the user sees their own accounts
  // Deduplication is applied to handle potential API duplicates
  const { visibleAccounts, archivedAccounts } = useMemo(() => {
    const seenIds = new Set<StrapiId>();
    const visible: ExchangeAccount[] = [];
    const archived: ExchangeAccount[] = [];
    for (const account of exchangeAccounts) {
      if (seenIds.has(account.id)) {
        continue;
      }
      seenIds.add(account.id);
      if (account.hidden) {
        archived.push(account);
      } else {
        visible.push(account);
      }
    }
    return { visibleAccounts: visible, archivedAccounts: archived };
  }, [exchangeAccounts]);

  const { state: generalStatsResponse, isLoading: isLoadingAccumulatedProfits } = useFetch(
    () =>
      userIdNum ? botConfigsEndpoints.getUserGeneralStats(userIdNum).then(({ data }) => data) : Promise.resolve(null),
    {
      deps: [userIdNum],
    }
  );
  // Endpoint returns data directly
  const perCurrency = generalStatsResponse?.perCurrency ?? {};
  const totals = generalStatsResponse?.totals;

  // Extract accumulated profits history and build currency breakdown for tooltip
  const { accumulatedProfitsPerCurrency, currencyBreakdown } = useMemo(() => {
    const profitsPerCurrency: Record<string, PerformancePoint[]> = {};
    const breakdown: CurrencyBreakdown[] = [];

    for (const [currency, stats] of Object.entries(perCurrency)) {
      if (stats.history) profitsPerCurrency[currency] = stats.history;
      if (stats.activeBalance) {
        breakdown.push({ currency, balance: stats.activeBalance });
      }
    }

    return {
      accumulatedProfitsPerCurrency: profitsPerCurrency,
      currencyBreakdown: breakdown,
    };
  }, [perCurrency]);

  // Use pre-calculated totals from API (with currency conversion)
  const activeBots = {
    mainCurrency: totals?.mainCurrency,
    totalBalance: totals?.balance,
    totalDeposits: totals?.deposits,
    totalWithdrawals: totals?.withdrawals,
    count: totals?.activeBots,
    openTrades: totals?.openTrades,
    hasMultipleCurrencies: totals?.hasMultipleCurrencies,
    currencyBreakdown,
  };

  const {
    referrals = [],
    isLoading: isLoadingReferrals,
    ...referralsProps
  } = useReferrals({
    depsToReset: [userId],
    pagination: { pageSize: 5 },
    filters: {
      expired: false,
      referrer: { id: userId },
    },
  });

  const {
    pagination: { total: totalReferrals },
  } = useReferrals({
    depsToReset: [userId],
    pagination: { pageSize: 1 },
    filters: { referrer: { id: userId } },
  });

  const {
    charges,
    isLoading: isLoadingCharges,
    ...chargesProps
  } = useCharges({
    depsToReset: [userId],
    filters: { pm_user: { id: userId } },
    pagination: { pageSize: 5 },
    sort: { createdAt: "down" },
  });

  const { claims, isLoading: isLoadingPayouts } = usePayouts({
    depsToReset: [userId],
    sort: { createdAt: "down" },
    filters: { showFailed: false, pm_user: { id: userId } },
    pagination: { pageSize: 5 },
  });

  const isPartner = referrerType ? ["AMBASSADOR", "INFLUENCER"].includes(referrerType) : false;
  const hasAffiliateActivity = isPartner || totalReferrals > 0 || (claims?.length ?? 0) > 0;

  // Handlers for grant modal
  const handleGrantFree = (exchangeAccount: { id: number | string; name?: string; [key: string]: unknown }) => {
    // Look up the full ExchangeAccount from our map; fall back to null if not found
    const fullAccount = exchangeAccountsById[exchangeAccount.id] ?? null;
    setSelectedExchangeAccount(fullAccount);
    setIsGrantModalOpen(true);
  };

  const handleGrantModalClose = () => {
    setIsGrantModalOpen(false);
    setSelectedExchangeAccount(null);
  };

  const handleGrantSuccess = () => {
    handleGrantModalClose();
    setRefreshKey((prev) => prev + 1);
  };

  // Handlers for VIP modal
  const handleDisableVIP = () => {
    setIsVIPModalOpen(true);
  };

  const handleVIPModalClose = () => {
    setIsVIPModalOpen(false);
  };

  const handleVIPSuccess = () => {
    handleVIPModalClose();
    refreshUser();
  };

  // Handlers for partnership modal
  const handleManagePartnership = () => {
    setIsPartnershipModalOpen(true);
  };

  const handlePartnershipModalClose = () => {
    setIsPartnershipModalOpen(false);
  };

  const handlePartnershipSuccess = () => {
    handlePartnershipModalClose();
    refreshUser();
  };

  // Handlers for manual billing modal
  const handleToggleManualBilling = () => {
    setIsManualBillingModalOpen(true);
  };

  const handleManualBillingModalClose = () => {
    setIsManualBillingModalOpen(false);
  };

  const handleManualBillingSuccess = () => {
    handleManualBillingModalClose();
    refreshUser();
  };

  // Handlers for assign referrer modal
  const handleAssignReferrer = () => {
    setIsAssignReferrerModalOpen(true);
  };

  const handleAssignReferrerModalClose = () => {
    setIsAssignReferrerModalOpen(false);
  };

  const handleAssignReferrerSuccess = () => {
    handleAssignReferrerModalClose();
    refreshUser();
  };

  const { state: requirements = [], isLoading: isLoadingRequirements } = useFetch(
    () => {
      if (!userIdNum) return Promise.resolve([]);
      return !isPartner
        ? referrerEndpoints.getRequirementsTo("AMBASSADOR", userIdNum).then(({ data }) => data.requirements)
        : referrerEndpoints.getRecurringRequirements(userIdNum).then(({ data }) => data.requirements);
    },
    { auto: !!referrerType && !!userIdNum, deps: [userIdNum, isPartner] }
  );

  return (
    <React.Fragment>
      <Header.BreadCrumb title={name} color="secondary" isDark={false} />

      <Container className="mt--6" fluid>
        {/* ========== USER OVERVIEW SECTION ========== */}
        <Row>
          <Col xs={12} lg={8}>
            <AdminUserOverviewCard
              user={user}
              referrer={referrer}
              onNavigateToReferrer={() => navigate(`/users/${referrer?.id}`)}
              onDisableVIP={user?.isVIP ? handleDisableVIP : undefined}
              onManagePartnership={handleManagePartnership}
              onToggleManualBilling={handleToggleManualBilling}
              onAssignReferrer={handleAssignReferrer}
              isLoading={isLoadingUser}
            />
          </Col>
          <Col xs={12} lg={4}>
            <StateSummary
              balance={activeBots?.totalBalance}
              numBots={activeBots?.count}
              numTrades={activeBots?.openTrades}
              withdrawal={activeBots?.totalWithdrawals}
              deposit={activeBots?.totalDeposits}
              currency={activeBots?.mainCurrency}
              hasMultipleCurrencies={activeBots?.hasMultipleCurrencies}
              currencyBreakdown={activeBots?.currencyBreakdown}
            />
          </Col>
        </Row>

        {/* ========== TRADING ACCOUNTS SECTION ========== */}
        {exchangeAccounts.length > 0 && (
          <>
            <SectionHeader
              title={t("tradingAccounts")}
              badge={visibleAccounts.length > 0 ? `${visibleAccounts.length} ${t("active")}` : null}
            />
            {/* Active accounts - always rendered */}
            {visibleAccounts.length > 0 && (
              <Row>
                {visibleAccounts.map(({ id }) => (
                  <Col key={id} xs={12} md={6} lg={4}>
                    <ExchangeAccountCard
                      key={`${id}-${refreshKey}`}
                      id={id}
                      onClick={() => navigate(`/users/${userId}/exchange-account/${id}`)}
                      isAdmin
                      onGrantFree={handleGrantFree}
                      bot={botsByExchangeAccountId[id]}
                      isLoadingBots={isLoadingBots}
                      exchangeAccount={exchangeAccountsById[id]}
                      isLoadingExchangeAccount={isLoadingExchangeAccounts}
                    />
                  </Col>
                ))}
              </Row>
            )}

            {/* Archived accounts - lazy loaded */}
            {archivedAccounts.length > 0 && (
              <>
                <div style={styles.archivedAccountsToggle}>
                  <span
                    style={styles.archivedToggleButton}
                    onClick={() => setShowArchivedAccounts(!showArchivedAccounts)}
                  >
                    <i className={`fas fa-${showArchivedAccounts ? "eye-slash" : "eye"} mr-2`} />
                    {showArchivedAccounts
                      ? t("hideArchivedAccounts", { count: archivedAccounts.length })
                      : t("showArchivedAccounts", { count: archivedAccounts.length })}
                    <i className={`fas fa-chevron-${showArchivedAccounts ? "up" : "down"} ml-2`} />
                  </span>
                </div>
                {showArchivedAccounts && (
                  <Row>
                    {archivedAccounts.map(({ id }) => (
                      <Col key={id} xs={12} md={6} lg={4}>
                        <ExchangeAccountCard
                          key={`${id}-${refreshKey}`}
                          id={id}
                          onClick={() => navigate(`/users/${userId}/exchange-account/${id}`)}
                          isAdmin
                          onGrantFree={handleGrantFree}
                          bot={botsByExchangeAccountId[id]}
                          isLoadingBots={isLoadingBots}
                          exchangeAccount={exchangeAccountsById[id]}
                          isLoadingExchangeAccount={isLoadingExchangeAccounts}
                        />
                      </Col>
                    ))}
                  </Row>
                )}
              </>
            )}
          </>
        )}

        {/* ========== BILLING & COMPLIANCE SECTION ========== */}
        <SectionHeader title={t("billingCompliance")} />
        <Row>
          <Col xs={12} lg={4}>
            <TaxProfileCard
              user={user}
              onUpdate={() => {
                refreshUser();
              }}
              isLoading={isLoadingUser}
              readOnly
            />
          </Col>
          <Col xs={12} lg={8}>
            <ChargesTableCard
              {...chargesProps}
              title={t("chargesHistory")}
              charges={charges}
              isLoading={isLoadingCharges}
            />
          </Col>
        </Row>

        {/* ========== AFFILIATE SECTION ========== */}
        {hasAffiliateActivity && (
          <div ref={affiliateRef}>
            <SectionHeader
              title={t("affiliateSystem")}
              badge={isPartner ? referrerType?.toLowerCase() : null}
              isOpen={isAffiliateOpen}
              onToggle={toggleAffiliate}
              collapsible
            />
            <Collapse isOpen={isAffiliateOpen}>
              <Row>
                <Col xs={12} lg={4}>
                  {!isPartner ? (
                    <AmbassadorRequirementsCard
                      requirements={requirements}
                      isLoading={isLoadingRequirements}
                      onSubmit={undefined}
                      compact
                    />
                  ) : (
                    <PartnerRecurringRequirementsCard
                      requirements={requirements}
                      isLoading={isLoadingRequirements}
                      compact
                    />
                  )}
                </Col>
                <Col xs={12} lg={8}>
                  <AdminReferralsTableCard
                    {...referralsProps}
                    totalReferrals={totalReferrals}
                    isLoading={isLoadingReferrals}
                    showCommissions={isPartner}
                    referrals={referrals}
                  />
                </Col>
              </Row>

              {(claims?.length ?? 0) > 0 && (
                <PayoutsTableCard title={t("affiliatePayouts")} claims={claims} isLoading={isLoadingPayouts} />
              )}
            </Collapse>
          </div>
        )}

        {/* ========== PERFORMANCE SECTION (Collapsible) ========== */}
        <div ref={performanceRef}>
          <SectionHeader
            title={t("performanceHistory")}
            isOpen={isPerformanceOpen}
            onToggle={togglePerformance}
            collapsible
          />
          <Collapse isOpen={isPerformanceOpen}>
            <Row>
              <Col xs={12}>
                <GeneralPerformanceGraphCard
                  data={accumulatedProfitsPerCurrency}
                  mainCurrency={activeBots?.mainCurrency}
                  isLoading={isLoadingAccumulatedProfits}
                />
              </Col>
            </Row>
          </Collapse>
        </div>
      </Container>

      {/* ========== MODALS ========== */}
      <EditFreePeriodModal
        isOpen={isGrantModalOpen}
        toggle={handleGrantModalClose}
        exchangeAccount={selectedExchangeAccount}
        onSuccess={handleGrantSuccess}
      />

      <DisableVIPModal isOpen={isVIPModalOpen} toggle={handleVIPModalClose} user={user} onSuccess={handleVIPSuccess} />

      <ManagePartnershipModal
        isOpen={isPartnershipModalOpen}
        toggle={handlePartnershipModalClose}
        user={user}
        onSuccess={handlePartnershipSuccess}
      />

      <ManualBillingModal
        isOpen={isManualBillingModalOpen}
        toggle={handleManualBillingModalClose}
        user={user}
        onSuccess={handleManualBillingSuccess}
      />

      <AssignReferrerModal
        isOpen={isAssignReferrerModalOpen}
        toggle={handleAssignReferrerModalClose}
        user={user as Parameters<typeof AssignReferrerModal>[0]["user"]}
        referrer={referrer as Parameters<typeof AssignReferrerModal>[0]["referrer"]}
        onSuccess={handleAssignReferrerSuccess}
      />
    </React.Fragment>
  );
};

const styles: Record<string, React.CSSProperties> = {
  sectionHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginTop: 32,
    marginBottom: 16,
    paddingBottom: 8,
    borderBottom: "1px solid #e5e7eb",
  },
  sectionHeaderClickable: {
    cursor: "pointer",
    userSelect: "none" as const,
  },
  sectionTitleRow: {
    display: "flex",
    alignItems: "center",
    gap: 12,
  },
  sectionTitle: {
    fontSize: 16,
    fontWeight: 600,
    color: "#374151",
    margin: 0,
    textTransform: "uppercase",
    letterSpacing: "0.5px",
  },
  sectionBadge: {
    fontSize: 11,
    fontWeight: 500,
    textTransform: "capitalize",
  },
  collapseToggle: {
    fontSize: 13,
    color: "#6b7280",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
  },
  archivedAccountsToggle: {
    display: "flex",
    justifyContent: "center",
    marginTop: 8,
    marginBottom: 16,
  },
  archivedToggleButton: {
    display: "flex",
    alignItems: "center",
    gap: 4,
    padding: "8px 16px",
    fontSize: 13,
    color: "#6b7280",
    backgroundColor: "#f3f4f6",
    borderRadius: 20,
    cursor: "pointer",
    transition: "all 0.2s ease",
  },
};

export default User;
